<template>
  <div class="d-flex flex-column flex-grow-1 overflow-hidden">
    <v-row no-gutters class="d-flex flex-grow-0">
      <participants-panel :researchers="researchers" />
    </v-row>
    <v-row
      no-gutters
      class="flex-column flex-grow-1 flex-nowrap overflow-hidden"
    >
      <div
        id="field-group-container"
        class="overflow-auto d-flex flex-column"
      >
        <reviewable-extraction-field-group
          v-for="(fieldGroup, i) in fieldGroups"
          :id="`field-group-${fieldGroup.id}`"
          :key="fieldGroup.id"
          :value="getExpansionState(fieldGroup)"
          :header-field="fieldGroup.header"
          :data-fields="fieldGroup.fields"
          :extraction-values="extractionValues"
          :reports="reports"
          :final-report="finalReport"
          :readonly="readonly"
          :class="{ ['bottom-border']: i < fieldGroups.length - 1 }"
          :error-field-ids="errorFieldIds"
          @confirm="$emit('confirm', $event)"
          @expand-next="onExpandNext($event, i, fieldGroup)"
          @input="onExpansion(fieldGroup, $event)"
        />
      </div>
    </v-row>
    <v-row v-if="!readonly" no-gutters class="flex-grow-0">
      <review-progress-panel
        class="flex-grow-1"
        :reviewed="reviewedCount"
        :total="fieldCount"
        :error="error"
      />
    </v-row>
    <v-row v-if="!readonly" no-gutters class="flex-grow-0">
      <data-extraction-panel-footer
        class="flex-grow-1"
        consensus-review
        @submit="$emit('submit')"
      />
    </v-row>
  </div>
</template>

<script>
import ParticipantsPanel from '@/components/DataExtraction/ParticipantsPanel';
import ReviewableExtractionFieldGroup from '@/components/DataExtraction/ReviewableExtractionFieldGroup';
import ReviewProgressPanel from '@/components/DataExtraction/ReviewProgressPanel';
import DataExtractionPanelFooter from '@/components/DataExtraction/DataExtractionPanelFooter';
import {
  DataExtractionFieldBase,
  DataExtractionFieldHeader,
  DataExtractionReport,
  DataExtractionValueBase,
  Researcher,
} from '@/models';
import { typeHelpers } from '@/modules';
import { splitFieldListIntoGroups } from '@/modules/dataExtractionFieldHelpers';

export default {
  name: 'ConsensusReviewPanel',
  components: {
    ParticipantsPanel,
    ReviewableExtractionFieldGroup,
    ReviewProgressPanel,
    DataExtractionPanelFooter,
  },
  props: {
    fields: {
      type: Array,
      default: () => [],
      validator: (x) =>
        typeHelpers.isArrayOfType(x, DataExtractionFieldBase),
    },
    researchers: {
      type: Array,
      default: () => [],
      validator: (x) => typeHelpers.isArrayOfType(x, Researcher),
    },
    extractionValues: {
      type: Array,
      default: () => [],
      validator: (x) =>
        typeHelpers.isArrayOfType(x, DataExtractionValueBase),
    },
    reports: {
      type: Array,
      default: () => [],
      validator: (x) =>
        typeHelpers.isArrayOfType(x, DataExtractionReport),
    },
    finalReport: {
      type: DataExtractionReport,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
    errorFieldIds: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({ expandedByField: {} }),
  computed: {
    reviewedCount() {
      return this.fields.filter((field) =>
        this.fieldHasFinalValue(field.id)
      ).length;
    },
    fieldCount() {
      return this.fields.filter(
        (field) => !(field instanceof DataExtractionFieldHeader)
      ).length;
    },
    fieldGroups() {
      return splitFieldListIntoGroups(this.fields);
    },
  },
  methods: {
    getExpansionState(fieldGroup) {
      return this.expandedByField[fieldGroup.id] || [];
    },
    onExpansion(fieldGroup, expansion) {
      this.$set(this.expandedByField, fieldGroup.id, expansion);
    },
    onExpandNext(lastFieldIndex, fieldGroupIndex, fieldGroup) {
      const expansion = this.getExpansionState(fieldGroup);
      if (lastFieldIndex === fieldGroup.fields.length - 1) {
        // If last field group, do nothing.
        if (fieldGroupIndex === this.fieldGroups.length - 1) {
          return;
        }

        // Expand the next field group.
        const nextGroup = this.fieldGroups[fieldGroupIndex + 1];
        this.$set(this.expandedByField, nextGroup.id, [0]);

        // Scroll to the next field group.
        this.$vuetify.goTo(`#field-group-${nextGroup.id}`, {
          container: '#field-group-container',
          offset: 95,
        });
      } else {
        // Expand the next field in the group.
        this.$set(this.expandedByField, fieldGroup.id, [
          ...expansion,
          lastFieldIndex + 1,
        ]);
      }
    },
    fieldHasFinalValue(fieldId) {
      return (
        this.finalReport &&
        this.finalReport.values.some(
          (value) => value.field.id === fieldId
        )
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';
.bottom-border {
  border-bottom: 1px solid map-get($grey, 'lighten-2') !important;
}
</style>

<style lang="scss">
// Hide last field divider to avoid clash with bottom field group border.
.reviewable-extraction-field-group
  .v-expansion-panels
  div:last-child
  .reviewable-extraction-field
  .divider:last-of-type {
  display: none;
}
</style>
